"use client";

import { useTranslations } from "next-intl";
import { ChangeEvent, ComponentPropsWithRef, FormEvent, ReactNode, SetStateAction, forwardRef, useEffect, useId, useMemo, useState } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";

import cn from "@/utils/cn";

export interface CheckBoxProps extends ComponentPropsWithRef<"input"> {
  name: string;
  label?: string;
  htmlLabel?: ReactNode;
  invertLabel?: boolean;
  field?: ControllerRenderProps;
  fieldState?: ControllerFieldState;
  error?: boolean;
  errorMessage?: string;
  note?: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckBoxProps>(
  ({ name, label, htmlLabel, invertLabel, field, fieldState, error, errorMessage, note, ...rest }, ref) => {
    const defaultID = useId();
    const t = useTranslations();

    const [isError, setIsError] = useState<boolean>(error!);
    const [displayError, setDisplayError] = useState<string>(errorMessage || "");

    const checkboxClass = cn("checkbox", rest?.className, invertLabel && "invert-label", isError && "border-[#B42318] text-[#B42318]");
    const [isChecked, setIsChecked] = useState(false);

    useEffect(() => {
      setIsError(fieldState?.error ? (!!fieldState.error as SetStateAction<boolean>) : error!);
    }, [error, fieldState]);

    useEffect(() => {
      if (fieldState && fieldState.error) {
        setDisplayError(t(fieldState.error.message) || "");
      }
    }, [fieldState, t]);

    const radioID = useMemo(() => {
      return rest?.id ? rest.id : defaultID;
    }, [rest, defaultID]);

    const fieldLabel = useMemo(() => {
      return (
        radioID && (
          <label htmlFor={radioID} className="w-full" onClick={() => setIsChecked(!isChecked)}>
            {!htmlLabel && label && <span dangerouslySetInnerHTML={{ __html: label }} />}
            {htmlLabel}
          </label>
        )
      );
    }, [radioID, label, htmlLabel, isChecked]);

    return (
      <div className="flex flex-col">
        <div className={checkboxClass}>
          <input
            onChange={(event: FormEvent<HTMLInputElement>) => {
              rest?.onChange?.(event as ChangeEvent<HTMLInputElement>);
              field?.onChange(event);
            }}
            id={radioID}
            ref={ref}
            {...rest}
            name={name}
            aria-label={label}
            type="checkbox"
            className="appearance-none"
          />
          {fieldLabel}
        </div>
        {(isError || note) && (
          <span
            className={`pl-xl h-m text-xs leading-[7px] font-secondary ${isError ? "text-[#B42318]" : invertLabel ? "text-gray-10" : "text-gray-70"}  pt-xs px-xxs`}
          >
            {isError ? displayError : note ? note : ""}
          </span>
        )}
      </div>
    );
  },
);
Checkbox.displayName = "Checkbox";
export default Checkbox;
